import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as LabReqActions from './lab-req.actions';
import { LabReqEntity } from './lab-req.models';

export const LAB_REQ_FEATURE_KEY = 'labReq';

export interface State extends EntityState<LabReqEntity> {
  selectedId?: string | number; // which LabReq record has been selected
  loading: boolean; // has the LabReq list is loading
  saving: boolean; // has the LabReq list is saving
  error?: string | null; // last known error (if any)
  limit: number;
  offset: number;
}

export interface LabReqPartialState {
  readonly [LAB_REQ_FEATURE_KEY]: State;
}

export function selectLabReqId(labReq: LabReqEntity): string {
  //In this case this would be optional since primary key is id
  return labReq._id;
}

export const labReqAdapter: EntityAdapter<LabReqEntity> =
  createEntityAdapter<LabReqEntity>({
    selectId: selectLabReqId
  });

export const initialState: State = labReqAdapter.getInitialState({
  // set initial required properties
  loading: false,
  saving: false,
  limit: 100,
  offset: 0
});

const labReqReducer = createReducer(
  initialState,
  on(LabReqActions.loadLabReq, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    LabReqActions.loadLabReqSuccess,
    (state, { loadType, labReqs, limit, offset }) => {
      return loadType === 'older'
        ? labReqAdapter.upsertMany(labReqs, {
            ...state,
            loading: false,
            limit,
            offset
          })
        : labReqAdapter.setAll(labReqs, {
            ...state,
            loading: false,
            limit,
            offset
          });
    }
  ),
  on(LabReqActions.loadLabReqFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false
  })),

  on(LabReqActions.saveLabReq, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(LabReqActions.saveLabReqSuccess, (state, { labReq }) =>
    labReqAdapter.addOne(labReq, { ...state, saving: false })
  ),
  on(LabReqActions.saveLabReqFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),

  on(LabReqActions.updateLabReq, state => ({
    ...state,
    saving: true,
    error: null
  })),
  on(LabReqActions.updateLabReqSuccess, (state, { labReq }) =>
    labReqAdapter.updateOne(
      { id: labReq._id, changes: labReq },
      { ...state, saving: false }
    )
  ),
  on(LabReqActions.updateLabReqFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error
  })),
  on(LabReqActions.setActiveLabReqId, (state, { labReqId }) => ({
    ...state,
    selectedId: labReqId
  })),
  on(LabReqActions.clearActiveLabReqId, state => ({
    ...state,
    selectedId: undefined
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return labReqReducer(state, action);
}
